const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

export type SupportCategory = "Feedback" | "Bug" | "Report" | "Idea" | "Contact";
export type SupportStatus = "open" | "resolved" | "closed";
export type SupportMessage = {
  id: string;
  author: "me" | "blackspace";
  body: string;
  createdAt: string;
  actorId?: string;
  authorKind?: "member" | "staff";
};
export type SupportCase = {
  id: string;
  category: SupportCategory;
  status: SupportStatus;
  lastPreview: string;
  lastMessageAt: string;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
  messages?: SupportMessage[];
  owner?: { id: string; username: string; displayName: string };
  reviewedBy?: string | null;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Sign in to contact Blackspace Support.");
  const response = await fetch(`${API_BASE}/api/support${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.message || "Support is unavailable right now.");
  return data as T;
}

export async function createSupportCase(category: SupportCategory, message: string) {
  return request<{ success: true; supportCase: SupportCase }>("/cases", {
    method: "POST", body: JSON.stringify({ category, message }),
  });
}

export async function listSupportCases(page = 1) {
  return request<{ success: true; cases: SupportCase[]; page: number; hasMore: boolean; total: number; unreadTotal: number }>(`/cases?page=${page}`);
}

export async function getSupportCase(id: string) {
  return request<{ success: true; supportCase: SupportCase }>(`/cases/${encodeURIComponent(id)}`);
}

export async function replyToSupportCase(id: string, message: string) {
  return request<{ success: true; supportCase: SupportCase }>(`/cases/${encodeURIComponent(id)}/messages`, {
    method: "POST", body: JSON.stringify({ message }),
  });
}

export async function markSupportCaseRead(id: string) {
  return request<{ success: true; supportCase: SupportCase }>(`/cases/${encodeURIComponent(id)}/read`, { method: "PATCH" });
}

export async function listStaffSupportCases(status: SupportStatus | "all" = "all", page = 1) {
  return request<{ success: true; cases: SupportCase[]; page: number; hasMore: boolean }>(`/admin/cases?status=${status}&page=${page}`);
}

export async function getStaffSupportCase(id: string) {
  return request<{ success: true; supportCase: SupportCase }>(`/admin/cases/${encodeURIComponent(id)}`);
}

export async function replyFromSupportStaff(id: string, message: string) {
  return request<{ success: true; supportCase: SupportCase }>(`/admin/cases/${encodeURIComponent(id)}/replies`, {
    method: "POST", body: JSON.stringify({ message }),
  });
}

export async function updateSupportCaseStatus(id: string, status: SupportStatus) {
  return request<{ success: true; supportCase: SupportCase }>(`/admin/cases/${encodeURIComponent(id)}/status`, {
    method: "PATCH", body: JSON.stringify({ status }),
  });
}
